<template>
  <div class="kjp-chart-wrap">
    <div v-if="absentSeriesNotes.length && !dismissed"
      class="alert alert-warning alert-dismissible py-2 px-3 small mb-2">
      <button type="button" class="close" aria-label="Close" @click="dismissed = true">
        <span aria-hidden="true">&times;</span>
      </button>
      <div v-for="(note, i) in absentSeriesNotes" :key="i">
        Not shown on the chart: {{ note.label }} — no dimensions have been recorded for any month,
        so no {{ note.type }} is drawn rather than a {{ note.type }} sitting on zero.
      </div>
    </div>

    <div class="kjp-chart-title">{{ chartTitle }}</div>

    <!-- One <svg> per panel, laid out by flex-wrap: side by side when the pane
         is wide enough for both, stacked when it is not. No breakpoint and no
         resize listener — the panels' own min-width decides. The SVGs are
         never hand-written here: buildScreenPanels (chartScreen.js) is the
         single builder for the on-screen chart, the way buildChartSvg
         (chartSvg.js) is for the Excel export. Both read the same
         chartSeries.js and chartGeometry.js, so they can differ in paint but
         never in figures. -->
    <div class="kjp-chart-panels">
      <div v-for="panel in panels" :key="panel.key" class="kjp-chart-panel" :style="frameStyle" v-html="panel.svg"></div>
    </div>
  </div>
</template>

<script>
import { buildScreenPanels, absentScreenSeriesNotes, screenChartTitle } from "@/components/kjp/chartScreen";
import { scopeColor, scopeTint } from "@/components/kjp/colors";
import dismissableNotice from "@/components/kjp/dismissableNotice";

export default {
  name: "kjp-chart",
  mixins: [dismissableNotice],
  props: {
    model: { type: Object, required: true },
    scope: { type: String, default: null },
  },
  computed: {
    scopeKey() {
      return this.scope || (this.model && this.model.scope);
    },
    chartTitle() {
      return screenChartTitle(this.scopeKey);
    },
    // Each panel is framed in its scope's colour (INFS-1454, Hanif feedback
    // 16 Oct): the same colour as the scope's tab, so tab, tab-panel edge and
    // chart frames read as one scope. Passed as custom properties so the rules
    // below stay plain CSS and colors.js stays the only place the hex lives.
    frameStyle() {
      return {
        "--frame-color": scopeColor(this.scopeKey),
        "--frame-tint": scopeTint(this.scopeKey, 0.1),
      };
    },
    panels() {
      return buildScreenPanels(this.model, this.scopeKey);
    },
    // Series the chart could not draw at all — every month unmeasured, so the
    // series is entirely absent rather than partially gapped (R06's usage line
    // in current production data). Computed by the same module that builds the
    // markup, from the same panel definitions, so the banner and the drawing
    // can never disagree about which series is missing.
    absentSeriesNotes() {
      return absentScreenSeriesNotes(this.model, this.scopeKey);
    },
  },
};
</script>

<style lang="scss" scoped>

// Bootstrap absolutely positions .close at top:0 with its own 0.75rem/1.25rem
// padding, which top-aligns the x against this alert's smaller py-2 and looks
// worse still when the message wraps to several lines. Centre it instead.
.alert-dismissible .close {
  top: 50%;
  transform: translateY(-50%);
  padding: 0 0.75rem;
  line-height: 1;
}

// Matches SCREEN_COLORS.surface in chartScreen.js. The chart used to render on
// a white card inside the dark dashboard, which is most of why it read badly
// (INFS-1454); this dark surface carries the panels, and keeps the space
// around them the same colour rather than framing them in white again.
.kjp-chart-wrap {
  background-color: #1b1b1b;
  padding: 0.75rem;
  // Printed output otherwise drops background fills to save ink; this is the
  // standard opt-out, and it covers the panels' tinted frames below too.
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.kjp-chart-title {
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
}

// The whole responsive behaviour, in three declarations: panels sit in a row
// and wrap when they no longer fit. `min-width` on the panel is what decides
// "no longer fits" — below it a second panel drops to its own line instead of
// both being squeezed to illegibility.
.kjp-chart-panels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

.kjp-chart-panel {
  flex: 1 1 450px;
  min-width: 410px;
  // Never stretch a lone panel across a wide pane — at some
  // width the type and marks stop gaining anything and the plot just looks
  // thin. Two panels still fill the row because each may grow to this.
  max-width: 650px;
  // The frame: a 2px border in the scope's colour round a low-alpha wash of
  // it over the dark surface (the SVG inside paints no canvas of its own, so
  // the wash shows through). Padding keeps the plot off the border. The
  // border and padding sit inside the flex-basis/min/max widths above
  // (border-box), so those were each raised by the 30px of frame to keep the
  // plot the width it had; side-by-side vs stacked still decides on min-width.
  border: 2px solid var(--frame-color);
  background-color: var(--frame-tint);
  border-radius: 6px;
  padding: 0.75rem;
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

// The <svg> arrives via v-html (see chartScreen.js), so it never gets this
// component's scoped data-v-* attribute — ::v-deep targets it anyway, the same
// idiom already used in src/views/Dashcam.vue for injected content.
.kjp-chart-panel ::v-deep svg {
  display: block;
  width: 100%;
  height: auto;
}

// The per-month hover bands. Each carries a <title>, so the browser shows that
// month's figures natively on hover; this makes the band visible while that
// happens, so the reader can see which column is being read.
.kjp-chart-panel ::v-deep .kjp-hit {
  cursor: crosshair;

  &:hover {
    fill: rgba(255, 255, 255, 0.06);
  }
}
</style>
